import React, { useState, useEffect, useImperativeHandle } from "react";
import { Card } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import PillTabs from "components/atoms/PillTabs/PillTabs";
import { downloadImportsTemplate } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { IPermissionProps } from "components/molecules/Navigation/interface/sidebar";
import Button from "components/atoms/Button/Button";
import { ImportRequestTypes } from "constants/";

type ImportHeaderProps = {
  onUploadClick: any;
  customRef: any;
};

function ImportHeader(props: ImportHeaderProps) {
  const intl = useIntl();
  const { onUploadClick, customRef } = props;
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const importType: string = searchParams.get("import_type") || "";

  const { permissions: permissionsUnparsed } = usePermissions();
  const permissions: IPermissionProps | null = permissionsUnparsed ? JSON.parse(permissionsUnparsed).menu_items : null;

  const isOffPlatformImport = importType === ImportRequestTypes.TRACKING;

  useImperativeHandle(customRef, () => ({ handleDownloadClick }));

  useEffect(() => {
    if (!importType) {
      searchParams.set("import_type", ImportRequestTypes.TRACKING);
      setSearchParams(searchParams);
    }
  }, [importType, searchParams, setSearchParams]);

  const handleDownloadClick = (showLoader = true) => {
    const fileName = {
      [ImportRequestTypes.QUOTE]: "import-quote-request",
      [ImportRequestTypes.QUICK]: "import-quick-request",
      [ImportRequestTypes.TRACKING]: "import-shipments",
    };
    if (showLoader) {
      setIsDownloading(true);
    }
    
    if (!isOffPlatformImport) {
      return downloadImportsTemplate()
        .then((res) => {
          const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.setAttribute("download", `${fileName[importType]}.xlsx`);
          link.click();
          return Promise.resolve();
        })
        .catch((err) => {
          errorToast(err, "Could not export file. Please try again later.");
          return Promise.reject();
        })
        .finally(() => {
          setIsDownloading(false);
        });
    }
  };

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("import_type");
    } else {
      searchParams.set("import_type", eventKey || "");
    }
    setSearchParams(searchParams);
  };

  return (
    <Card
      className="w-100 import-header"
      style={{ padding: 16, paddingBottom: 0, borderRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
    >
      <h3 className="d-none d-md-block" style={{ marginBottom: "16px" }}>
        <FormattedMessage id="imports.title" defaultMessage="Imports" />
      </h3>
      <div className=" d-flex justify-content-between align-items-center">
        <PillTabs
          id="import-tabs"
          activeKey={importType}
          onSelect={handleStatusChange}
          tabItems={[
            // TODO: BUG-1725 uncomment when import works
            // ...(permissions?.view_quick_requests
            //   ? [
            //       {
            //         eventKey: ImportRequestTypes.QUICK,
            //         title: intl.formatMessage({
            //           id: "components.molecules.navigation.quickRequest",
            //           defaultMessage: "Quick Request",
            //         }),
            //       },
            //     ]
            //   : []),
            // ...(permissions?.view_quote_requests
            //   ? [
            //       {
            //         eventKey: ImportRequestTypes.QUOTE,
            //         title: intl.formatMessage({
            //           id: "components.molecules.navigation.quoteRequest",
            //           defaultMessage: "Quote Request",
            //         }),
            //       },
            //     ]
            //   : []),
            ...(permissions?.view_shipments
              ? [
                  {
                    eventKey: ImportRequestTypes.TRACKING,
                    title: intl.formatMessage({
                      id: "components.molecules.navigation.Trackings",
                      defaultMessage: "Trackings",
                    }),
                  },
                ]
              : []),
          ]}
        />
        <div className="d-flex flex-column align-items-center justify-content-between">
          <Button
            className="rounded-pill"
            label={intl.formatMessage({
              id: "tracking.components.updates.upload",
              defaultMessage: "Upload",
            })}
            type="button"
            onClick={onUploadClick}
          />
          
          {!isOffPlatformImport && (
            !isDownloading ? (
              <Button
                className="text-primary text-decoration-underline download-btn"
                label={intl.formatMessage({
                  id: "imports.downloadTemplate",
                  defaultMessage: "Download Template",
                })}
                variant="link"
                type="button"
                onClick={() => handleDownloadClick()}
              />
            ) : (
              <div style={{ width: 138, display: "flex", justifyContent: "center", marginTop: 14 }}>
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            )
          )}
        </div>
      </div>
    </Card>
  );
}

export default ImportHeader;
